import React, { useEffect } from "react";

import LandingHeader from "../header";

import "./privacyPolicy.css";
import Footer from "../footer";

function PrivacyPolicy() {
  // Scrolls to the top of the page when the component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="privacy-policy-wrapper">

      {/* Website Header */}
      <div className="privacy-policy-header">
        <LandingHeader />
      </div>

      {/* Page Title */}
      <div className="privacy-policy-title">
        <h1>Privacy Policy</h1>

        <p>
          Last Updated: September 13, 2026
        </p>
      </div>

      {/* Policy Content */}
      <div className="privacy-policy-content">

        <section>
          <h2>1. Introduction</h2>

          <p>
            HMW Communications Private Limited ("HMW Communications",
            "we", "us", or "our") respects your privacy and is committed
            to protecting the personal information you provide to us or
            that we collect when you use our websites, applications,
            products, and services.
          </p>

          <p>
            This Privacy Policy explains how we collect, use, store,
            disclose, and protect personal information in connection with
            our products and services, including:
          </p>

          <ul>
            <li>
              <strong>QuallyHire AI</strong> – our AI-powered hiring and
              recruitment platform.
            </li>

            <li>
              <strong>NuJobs</strong> – our job search and recruitment
              platform for candidates and employers.
            </li>
          </ul>

          <p>
            By accessing or using our websites or services, you acknowledge
            that you have read and understood this Privacy Policy.
          </p>
        </section>

        <section>
          <h2>2. Scope of This Privacy Policy</h2>

          <p>
            This Privacy Policy applies to personal information collected
            through our websites, web applications, registration processes,
            recruitment services, job listings, communications, and other
            services provided by HMW Communications.
          </p>

          <p>
            Different users may interact with our services in different
            ways. For example, an employer may use QuallyHire AI to manage
            recruitment activities, while a candidate may use NuJobs to
            search and apply for jobs.
          </p>

          <p>
            Accordingly, the type of information collected and the way it
            is used may depend on how you use our services.
          </p>
        </section>

        <section>
          <h2>3. Information We Collect</h2>

          <p>
            Depending on your interaction with our services, we may collect
            the following categories of information.
          </p>

          <h3>3.1 Account and Contact Information</h3>

          <ul>
            <li>Name</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Login credentials and account information</li>
            <li>Company or organization information</li>
            <li>Other information you voluntarily provide to us</li>
          </ul>

          <h3>3.2 Candidate Information</h3>

          <p>
            Candidates using NuJobs or participating in recruitment
            processes may provide information such as:
          </p>

          <ul>
            <li>Resume and CV information</li>
            <li>Employment history</li>
            <li>Education information</li>
            <li>Professional skills</li>
            <li>Career preferences</li>
            <li>Job applications</li>
            <li>Professional experience</li>
            <li>Candidate profile information</li>
          </ul>

          <h3>3.3 Employer and Recruiter Information</h3>

          <ul>
            <li>Company information</li>
            <li>Recruiter information</li>
            <li>Job descriptions</li>
            <li>Job requirements</li>
            <li>Candidate recruitment information</li>
            <li>Recruitment workflow information</li>
            <li>Evaluation and hiring-related information</li>
          </ul>

          <h3>3.4 Technical and Usage Information</h3>

          <p>
            When you access our websites or applications, certain technical
            information may be collected automatically, including:
          </p>

          <ul>
            <li>IP address</li>
            <li>Browser type and version</li>
            <li>Device information</li>
            <li>Operating system</li>
            <li>Pages viewed</li>
            <li>General usage information</li>
            <li>Log and diagnostic information</li>
          </ul>
        </section>

        <section>
          <h2>4. How We Use Personal Information</h2>

          <p>
            We may use personal information for purposes including:
          </p>

          <ul>
            <li>Providing and operating our services.</li>

            <li>Creating and managing user accounts.</li>

            <li>Processing job applications.</li>

            <li>Publishing and managing job listings.</li>

            <li>Supporting recruitment and hiring workflows.</li>

            <li>
              Processing and parsing resumes and candidate profiles.
            </li>

            <li>
              Extracting and organizing information such as skills,
              education, employment history, professional experience,
              and other candidate information.
            </li>

            <li>
              Supporting job search, candidate search, job matching,
              recommendations, and recruitment-related functionality.
            </li>

            <li>
              Helping employers and recruiters identify candidates
              relevant to job requirements.
            </li>

            <li>Improving our products and services.</li>

            <li>Providing customer and technical support.</li>

            <li>Communicating with users about our services.</li>

            <li>Maintaining security and preventing misuse.</li>

            <li>Detecting fraudulent or unauthorized activity.</li>

            <li>Complying with applicable legal obligations.</li>
          </ul>
        </section>

        <section>
          <h2>5. QuallyHire AI and QHRFlow</h2>

          <p>
            QuallyHire AI, including QHRFlow, provides recruitment,
            applicant tracking, candidate management, and hiring workflow
            functionality to organizations, recruiters, staffing companies,
            and other users involved in recruitment activities.
          </p>

          <p>
            Information processed through QHRFlow may include job
            descriptions, candidate resumes, candidate profiles,
            applications, recruiter activities, recruitment workflows,
            evaluations, interview information, hiring-related information,
            and other information provided by customers or candidates.
          </p>

          <p>
            QHRFlow may process and organize candidate information to
            support activities such as resume parsing, candidate search,
            job and candidate matching, shortlisting, recruitment workflow
            management, and other recruitment-related functionality.
          </p>

          <p>
            Customers using QHRFlow are responsible for ensuring that
            they have the appropriate rights, permissions, notices, and
            legal basis to provide candidate information to the platform
            and to process that information for their recruitment
            activities.
          </p>
        </section>

        <section>
          <h2>6. NuJobs</h2>

          <p>
            NuJobs is a job search and recruitment platform that enables
            candidates to create profiles, upload resumes, discover job
            opportunities, save jobs, receive relevant job recommendations,
            and apply for jobs posted by employers and recruiters.
          </p>

          <p>
            Depending on how you use NuJobs, we may process information
            such as your candidate profile, resume, employment history,
            education, skills, career preferences, job searches,
            applications, saved jobs, and other information that you
            voluntarily provide.
          </p>

          <p>
            NuJobs may process and organize information contained in your
            resume or profile, including skills, education, professional
            experience, employment information, and other recruitment-related
            information, to provide job search, job matching, recommendations,
            application processing, and related services.
          </p>

          <p>
            When you apply for a job through NuJobs, relevant profile,
            resume, and application information may be made available to
            the employer, recruiter, staffing organization, or other
            authorized user associated with that job opportunity.
          </p>

          <p>
            Candidate information may also be made available to authorized
            employers or recruiters where permitted by your profile settings
            and where necessary to provide recruitment-related services.
          </p>
        </section>

        <section>
          <h2>7. AI and Automated Processing</h2>

          <p>
            QuallyHire AI, QHRFlow, NuJobs, and related services may use
            automated technologies, algorithms, and artificial intelligence
            to support recruitment and job-search related activities.
          </p>

          <p>
            Depending on the service being used, automated processing may
            assist with activities such as parsing resumes, extracting and
            organizing candidate information, identifying skills, education
            and professional experience, analyzing job descriptions,
            supporting candidate search, job matching, candidate matching,
            job recommendations, and recruitment workflows.
          </p>

          <p>
            Automated processing may also be used to organize or rank
            information based on its relevance to job requirements,
            candidate preferences, skills, experience, or other
            recruitment-related criteria.
          </p>

          <p>
            Automated or AI-generated results are intended to assist
            candidates, employers, and recruiters. Such results may not
            always be complete or accurate and should not necessarily be
            considered a final hiring or employment decision.
          </p>

          <p>
            Employers and recruiters remain responsible for their
            hiring, employment, recruitment, shortlisting, interview,
            selection, and other employment-related decisions.
          </p>
        </section>

        <section>
          <h2>8. Sharing of Personal Information</h2>

          <p>
            We do not sell personal information as a general business
            practice.
          </p>

          <p>
            We may share information where necessary to provide our
            services, facilitate recruitment activities, operate our
            technology infrastructure, maintain security, or comply with
            applicable law.
          </p>

          <p>
            Depending on how you use our services, information may be
            shared with:
          </p>

          <ul>
            <li>
              Employers and recruiters involved in recruitment activities.
            </li>

            <li>
              Service providers supporting our technology and operations.
            </li>

            <li>
              Hosting, infrastructure, security, analytics, communication,
              or other technology providers where applicable.
            </li>

            <li>
              Government authorities or other parties where disclosure is
              required by law.
            </li>
          </ul>
        </section>

        <section>
          <h2>9. Third-Party Service Providers</h2>

          <p>
            We may use third-party service providers to support the
            operation of our websites and services.
          </p>

          <p>
            Such providers may support areas including hosting,
            infrastructure, analytics, communications, authentication,
            security, payment processing, or other operational functions.
          </p>

          <p>
            Where third parties process personal information on our behalf,
            we expect them to handle such information in accordance with
            applicable agreements and legal requirements.
          </p>
        </section>

        <section>
          <h2>10. Cookies and Similar Technologies</h2>

          <p>
            Our websites and services may use cookies, local storage,
            web beacons, pixels, and similar technologies.
          </p>

          <p>
            These technologies may be used to maintain user sessions,
            remember preferences, understand website usage, improve
            functionality, maintain security, measure performance, and
            support advertising activities.
          </p>

          <p>
            You may be able to control cookies through your browser or
            device settings. Disabling certain cookies may affect the
            functionality of some parts of our websites or services.
          </p>
        </section>

        <section>
          <h2>11. Advertising and Google AdSense</h2>

          <p>
            NuJobs may display advertisements provided by third-party
            advertising providers, including Google AdSense.
          </p>

          <p>
            Third-party vendors, including Google, may use cookies,
            web beacons, IP addresses, or similar technologies to serve
            and measure advertisements and to understand interactions
            with advertisements.
          </p>

          <p>
            Depending on applicable settings, user activity and available
            advertising technologies, advertisements may be personalized
            based on a user's previous visits to this website or other
            websites.
          </p>

          <p>
            Google provides users with controls for managing personalized
            advertising through its advertising settings.
          </p>

          <p>
            For more information about how Google uses data when you use
            websites or apps that use its services, please refer to
            Google's published privacy information.
          </p>

          <p>
            Our advertising practices may change as we add or remove
            advertising providers or services. We will update this policy
            where appropriate.
          </p>
        </section>

        <section>
          <h2>12. Data Security</h2>

          <p>
            We take reasonable technical and organizational measures to
            protect personal information against unauthorized access,
            alteration, disclosure, loss, or destruction.
          </p>

          <p>
            However, no method of electronic transmission or storage is
            completely secure. Therefore, while we take reasonable steps
            to protect information, we cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>13. Data Retention</h2>

          <p>
            We retain personal information for as long as reasonably
            necessary to provide our services, maintain business records,
            support recruitment activities, resolve disputes, enforce
            agreements, maintain security, and comply with applicable
            legal obligations.
          </p>

          <p>
            Retention periods may differ depending on the type of
            information and the purpose for which it was collected.
          </p>
        </section>

        <section>
          <h2>14. Your Privacy Choices and Rights</h2>

          <p>
            Depending on applicable law and your relationship with us,
            you may have rights relating to your personal information,
            including rights to request access, correction, deletion,
            or other appropriate handling of your information.
          </p>

          <p>
            You may also be able to manage certain browser cookies and
            advertising preferences through your browser, device, or
            relevant advertising provider settings.
          </p>

          <p>
            To submit a privacy-related request, please contact us using
            the information provided in the Contact Us section below.
          </p>
        </section>

        <section>
          <h2>15. Third-Party Websites and Links</h2>

          <p>
            Our websites and services may contain links to websites or
            services operated by third parties.
          </p>

          <p>
            We are not responsible for the privacy practices, content,
            security, or policies of third-party websites. We encourage
            you to review the privacy policies of third-party services
            before providing them with personal information.
          </p>
        </section>

        <section>
          <h2>16. Children's Privacy</h2>

          <p>
            Our services are intended for professional recruitment,
            employment, and job-search activities and are not directed
            toward children.
          </p>

          <p>
            We do not knowingly collect personal information from children
            where prohibited by applicable law.
          </p>
        </section>

        <section>
          <h2>17. International Data Processing</h2>

          <p>
            Depending on the infrastructure and service providers used to
            operate our services, personal information may be processed
            or stored in locations outside the country in which you reside.
          </p>

          <p>
            Where applicable, we take reasonable steps to ensure that
            personal information is handled in accordance with applicable
            legal and contractual requirements.
          </p>
        </section>

        <section>
          <h2>18. Changes to This Privacy Policy</h2>

          <p>
            We may update this Privacy Policy from time to time to reflect
            changes in our services, technology, business practices, or
            applicable legal requirements.
          </p>

          <p>
            When we make changes, we will update the "Last Updated" date
            displayed at the beginning of this Privacy Policy.
          </p>

          <p>
            We encourage you to review this page periodically to remain
            informed about how we handle personal information.
          </p>
        </section>

        <section>
          <h2>19. Contact Us</h2>

          <p>
            If you have questions about this Privacy Policy, our privacy
            practices, or wish to submit a privacy-related request, you
            can contact us at:
          </p>

          <div className="privacy-contact">
            <strong>HMW Communications Private Limited</strong>

            <br />

            Email: privacy@hmwcomm.com

            <br />

            Website: QuallyHire AI / NuJobs
          </div>
        </section>

      </div>

      {/* Website Footer */}
      <Footer />

    </div>
  );
}

export default PrivacyPolicy;